import * as React from "react"
import { LoadingBlock } from "@digits-shared/components/Loaders"
import { ColorIcon } from "@digits-shared/components/UI/Elements/ColorIcon"
import { IconShape } from "@digits-shared/components/UI/Icons/Icon"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { SidebarRow } from "src/frontend/components/Shared/NavSidebar/SidebarExpando"
import { SidebarHeader } from "src/frontend/components/Shared/NavSidebar/SidebarHeader"
import { useCurrentAffiliation } from "src/shared/hooks/useCurrentAffiliation"

/*
  STYLES
*/

const BrandingHeader = styled.div`
  background: ${colors.translucentWhite10};
  width: 100%;
  padding: 1px 2px 4px;
  .digits-mac & {
    padding-top: 40px;
  }
`

const HeaderRow = styled(SidebarRow)`
  padding: 4px 3px;
  margin-left: 14px;

  &&::before {
    content: "";
    width: 0;
  }
`

const LabelContainer = styled.div`
  text-align: left;
  position: relative;
`

const PoweredBy = styled.div`
  font-weight: ${fonts.weight.medium};
  font-size: 10px;
  line-height: 14px;
  color: ${colors.secondary70};
  padding-right: 10px;
`

const OrgName = styled.div`
  font-weight: ${fonts.weight.heavy};
  font-size: 16px;
  line-height: 18px;
  color: ${colors.secondary};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal;
  // maintain width to prevent adding lines when sidebar collapses
  min-width: 156px;
`

const BookmarkContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${colors.white};
  width: 48px;
  min-width: 48px;
  height: 48px;
  border-radius: 11px;
  overflow: hidden;
  margin-left: -12px;
`

const Logo = styled.img`
  max-width: 48px;
  max-height: 48px;
`

/*
  COMPONENT
*/

export const SidebarBrandedHeader: React.FC = () => (
  <>
    <BrandingHeader>
      <AffiliationBadge />
    </BrandingHeader>

    <SidebarHeader />
  </>
)

const AffiliationBadge: React.FC = () => {
  const currentAccountingFirm = useCurrentAffiliation()

  if (!currentAccountingFirm) {
    return <AffiliationLoading />
  }

  return (
    <HeaderRow
      element={
        <Bookmark
          iconUrl={currentAccountingFirm?.organization.iconUrl}
          fallbackText={currentAccountingFirm.organization.name}
        />
      }
    >
      <LabelContainer>
        <PoweredBy>Powered by</PoweredBy>
        <OrgName>{currentAccountingFirm?.organization.name}</OrgName>
      </LabelContainer>
    </HeaderRow>
  )
}

const Bookmark: React.FC<{
  iconUrl?: string | null
  fallbackText: string
}> = ({ iconUrl, fallbackText }) => (
  <BookmarkContainer>
    {iconUrl && <Logo src={iconUrl} />}
    {!iconUrl && <ColorIcon fallbackText={fallbackText} iconShape={IconShape.Square} size={48} />}
  </BookmarkContainer>
)

const AffiliationLoading: React.FC = () => (
  <HeaderRow
    element={
      <BookmarkContainer>
        <LoadingBlock borderRadius="11px" height="48px" width="48px" margin-left="12px" />
      </BookmarkContainer>
    }
  >
    <LabelContainer>
      <PoweredBy>Powered by</PoweredBy>
      <OrgName>
        <LoadingBlock height="15px" width="134px" />
      </OrgName>
    </LabelContainer>
  </HeaderRow>
)
